import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { passwordResetService } from '../services/passwordReset';

const ForgotPassword: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setMessage('');

        if (!email.trim()) {
            setError(t('forgotPassword.errorMessage'));
            setIsLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError(t('forgotPassword.errorMessage'));
            setIsLoading(false);
            return;
        }

        try {
            const response = await passwordResetService.requestPasswordReset(email);
            
            if (response.success) {
                setMessage(response.message);
                // Optionally redirect after a delay
                // setTimeout(() => navigate('/login'), 3000);
            } else {
                setError(response.message);
            }
        } catch (error) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <motion.div
                    className="auth-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="auth-header">
                        <h1>{t('forgotPassword.title')}</h1>
                        <p>{t('forgotPassword.subtitle')}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                        <div className="form-group">
                            <label htmlFor="email">{t('forgotPassword.email')}</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('forgotPassword.emailPlaceholder')}
                                required
                                className="input"
                            />
                        </div>

                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}

                        {message && (
                            <motion.div
                                className="success-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {message}
                            </motion.div>
                        )}

                        <motion.button
                            type="submit"
                            className="btn btn-primary auth-btn"
                            disabled={isLoading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {isLoading ? t('forgotPassword.sending') : t('forgotPassword.sendResetLink')}
                        </motion.button>
                    </form>

                    <div className="auth-footer">
                        <p>
                            {t('forgotPassword.rememberPassword')}{' '}
                            <Link to="/login" className="auth-link">
                                {t('forgotPassword.backToLogin')}
                            </Link>
                        </p>
                        <p>
                            {t('forgotPassword.noAccount')}{' '}
                            <Link to="/register" className="auth-link">
                                {t('forgotPassword.createAccount')}
                            </Link>
                        </p>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ForgotPassword;
